import * as React from 'react';
import { debounce } from '@agilepoint/utils-common';
import { ICalloutContentProps } from './callout-types';
import { getCalloutPosition } from './callout-utils';
import {defaultProps} from './callout-default-props';
import { CalloutLayer, CalloutWrapper, CalloutContentWrapper, CalloutBeak } from './callout-parts';
import { withHostableSurface } from '../hostable-surface';
import { IntervalTimer, KeyStrokeListener, isTargetElementUnderContainer } from '../../utils';

class CalloutContentBase extends React.PureComponent<ICalloutContentProps> {
    private rootDOMElement: React.RefObject<HTMLDivElement>;
    private timer?: number;
    private positionComputationVersion = 1;
    private readonly _updateTimer: IntervalTimer;
    private keyStroke?: KeyStrokeListener;

    private _onSurfaceResize = debounce(() => {
        const { onResize } = this.props;
        this.positionComputationVersion++;
        this.forceUpdate();
        if (onResize !== undefined) {
            onResize();
        }
    }, 100, this);

    private _refocusTarget = debounce(() => {
        const { targetRef, disableRefocusTarget } = this.props;
        if (disableRefocusTarget) {
            return;
        }
        targetRef?.current?.focus();
    }, 100, this);

    constructor(props: ICalloutContentProps) {
        super(props);
        this._updateTimer = new IntervalTimer(this.checkPickerPosition);
        this.rootDOMElement = props.elementRef || React.createRef();
        this.keyStroke = props.closeOnEscape ? new KeyStrokeListener(this.rootDOMElement) : undefined;
    }

    componentDidMount() {
        const { preventDismissOnFocusOut, onOpen, addSurfaceEventListener, autofocusOnOpen } = this.props;
        if (!preventDismissOnFocusOut) {
            this.timer = setTimeout(addSurfaceEventListener, 0, 'surfaceClick', this._onSurfaceClickHandler);
        }
        addSurfaceEventListener('surfaceResize', this._onSurfaceResize);
        this.manageTimerUpdation();
        if (onOpen !== undefined) {
            onOpen();
        }
        if (autofocusOnOpen) {
            this.rootDOMElement.current?.focus();
        }
        this.keyStroke?.subscribe(this._escapeKeyClickHandler, 'Escape');
    }

    componentDidUpdate(prevProps: ICalloutContentProps) {
        const { preventDismissOnFocusOut, addSurfaceEventListener, removeSurfaceEventListener } = this.props;
        this.manageTimerUpdation();
        if (prevProps.preventDismissOnFocusOut === preventDismissOnFocusOut) {
            return;
        }
        if (!preventDismissOnFocusOut) {
            clearTimeout(this.timer);
            this.timer = setTimeout(addSurfaceEventListener, 0, 'surfaceClick', this._onSurfaceClickHandler);
            return;
        }
        removeSurfaceEventListener('surfaceClick', this._onSurfaceClickHandler);
    }

    componentWillUnmount() {
        const { removeSurfaceEventListener } = this.props;
        this.keyStroke?.unsubscribe();
        removeSurfaceEventListener('surfaceClick', this._onSurfaceClickHandler);
        removeSurfaceEventListener('surfaceResize', this._onSurfaceResize);
        clearTimeout(this.timer);
        this._refocusTarget();
    }

    render() {
        const { showModal } = this.props;
        const { layerStyles } = getCalloutPosition(this.positionComputationVersion, this.props);
        return (
            <CalloutLayer layerStyles={layerStyles} showModal={showModal}>
                {this.renderCallout()}
            </CalloutLayer>
        );
    }

    private manageTimerUpdation() {
        const { targetRef } = this.props;
        if (targetRef && targetRef.current) {
            this._updateTimer.start(100, true);
            return;
        }
        this._updateTimer.stop();
    }

    private checkPickerPosition = () => {
        const { targetRef } = this.props;
        if (!targetRef || !targetRef.current) {
            return;
        }

        const targetRect = targetRef.current?.getBoundingClientRect();
        const rootElementRect = this.rootDOMElement.current?.getBoundingClientRect();
        if (!targetRect || !rootElementRect) {
            return;
        }

        const overlappedYBy = Math.round(rootElementRect.top - targetRect.bottom);
        if (overlappedYBy < 0 || overlappedYBy > 1) {
            this.positionComputationVersion++;
            this.forceUpdate();
        }
        this._updateTimer.stop();
    }

    private renderCallout() {
        const { className, isBeakVisible, animateOnOpen = defaultProps.animateOnOpen, showModal } = this.props;
        const { contentPositionStyles } = getCalloutPosition(this.positionComputationVersion, this.props);
        return (
            <CalloutWrapper $animateOnOpen={animateOnOpen} $contentPositionStyles={contentPositionStyles} role="dialog"
                className={className} ref={this.rootDOMElement} $showModal={showModal} onClick={this.onWarpperClick}>
                {isBeakVisible ? this.renderBeak() : null}
                {this.renderContent()}
            </CalloutWrapper>
        );
    }

    private onWarpperClick = (ev: React.MouseEvent) => {
        const { preventClickEventPropagation } = this.props;
        if (preventClickEventPropagation) {
            ev.stopPropagation();
        }
    }

    private renderContent() {
        const { children } = this.props;
        const { contentPositionStyles } = getCalloutPosition(this.positionComputationVersion, this.props);
        return (
            <CalloutContentWrapper onContextMenu={this._onContextMenu} $contentPositionStyles={contentPositionStyles}>
                {children}
            </CalloutContentWrapper>
        );
    }

    private renderBeak() {
        const { beakWidth = defaultProps.beakWidth } = this.props;
        const { beakStyles } = getCalloutPosition(this.positionComputationVersion, this.props);
        return <CalloutBeak beakStyles={beakStyles} beakWidth={beakWidth} />;
    }

    private _onSurfaceClickHandler = (ev: MouseEvent) => {
        const { onDismiss } = this.props;
        if (onDismiss === undefined || isTargetElementUnderContainer(this.rootDOMElement, ev.target as Node)) {
            return;
        }
        onDismiss();
    }

    private _escapeKeyClickHandler = () => {
        const { onDismiss } = this.props;
        if (onDismiss === undefined) {
            return;
        }
        onDismiss();
    }

    private _onContextMenu = (e: React.MouseEvent) => {
        const { disableContextMenu } = this.props;
        if (disableContextMenu) {
            e.preventDefault();
            e.stopPropagation();
        }
    }
}

export const CalloutContent = withHostableSurface(CalloutContentBase);
